import { GetEventsResponse } from '@/api/Event'
import type { Session } from '@/api/Sessions'
import { assignGroupColor } from '@/helpers/TicketGroupsColors'

export interface LinkedTG extends TicketGroup {
  types: LinkedTT[]
  meta: MetaData
  color: number
}

export interface LinkedTT extends TicketType {
  group: LinkedTG
}

export interface EventDetails extends EventTemplate {
  ticketGroups: LinkedTG[]
  ticketTypes: TicketType[]
  venue: Venue
  meta: MetaData
  messageRules?: MessageRule[]
}

/**
 * @deprecated Use EventDetails. Sessions should not be bundled with Event API responses.
 * @see https://drive.google.com/file/d/1rPlkpO5Fz5t7JL19AEqzZTfzqGzUdbmg/view?usp=sharing
 */
export interface NestedEventDataWithSessions extends EventDetails {
  sessions: Session[]
}

/**
 * @deprecated Use EventDetails. Sessions should not be bundled with Event API responses.
 * @see https://drive.google.com/file/d/1rPlkpO5Fz5t7JL19AEqzZTfzqGzUdbmg/view?usp=sharing
 */
export interface NestedEventDataFirstSession extends EventDetails {
  firstSession: Session
}

export function nestEventsApiGetEventsResponse(
  response: GetEventsResponse,
): NestedEventDataWithSessions[] | NestedEventDataFirstSession[] {
  const { eventTemplates, eventSessions, meta, ticketTypes, ticketGroups, venues } = response
  const groups = linkGroupsToTypes(ticketGroups, ticketTypes)
  const withGroups = linkTemplatesToTGs(eventTemplates, groups)
  const withSessions = linkTemplatesToSessions(withGroups, eventSessions)
  const withMeta = linkTemplatesToMetadata(withSessions, meta)
  return withMeta.map((template: any) => {
    template.venue = venues.find((venue) => venue.id === template.venue_id)
    return template
  })
}

export type TicketGroupsAndTypesResponse = ApiResponse<'ticket_group' | 'ticket_type' | 'meta'>

export function toLinkedGroups(response: TicketGroupsAndTypesResponse): LinkedTG[] {
  const r = apiEntities(response)
  return linkTGRelations(r.ticket_group, r.meta, r.ticket_type)
}

export function linkTGRelations(ticketGroups: TicketGroup[], meta: MetaEntity[], types: TicketType[]): LinkedTG[] {
  const withMeta = linkToMetadata('ticket_group', ticketGroups, meta)
  const clonedTypes = types.map((type) => ({ ...type }))
  const result = linkGroupsToTypes(withMeta, clonedTypes) as LinkedTG[]
  linkTypesBackToGroups(result)
  return result.map(assignGroupColor)
}

/**
 * Mutates the passed in array of ticket groups.
 */
function linkTypesBackToGroups(groups: LinkedTG[]): void {
  for (const group of groups) {
    for (const type of group.types) {
      type.group = group
    }
  }
}

function linkGroupsToTypes<G extends TicketGroup, Result extends G & { types: TicketType[] }>(
  groups: G[],
  types: TicketType[],
): Result[] {
  return linkRelatedEntities(groups, types, 'ticket_group_id', 'types') as Result[]
}

function linkTemplatesToTGs<T extends EventTemplate, Result extends T & { ticketGroups: TicketGroup[] }>(
  templates: T[],
  groups: TicketGroup[],
): Result[] {
  return linkRelatedEntities(templates, groups, 'event_template_id', 'ticketGroups') as Result[]
}

function linkTemplatesToSessions<T extends EventTemplate, Result extends T & { sessions: EventSession[] }>(
  templates: T[],
  sessions: EventSession[],
): Result[] {
  return linkRelatedEntities(templates, sessions, 'event_template_id', 'sessions') as Result[]
}

export function linkTemplateToMetadata(template: EventTemplate, metadataEntities: MetaEntity[]): EventWithMeta {
  const [result] = linkTemplatesToMetadata([template], metadataEntities)
  return result
}

export function linkTemplatesToMetadata(templates: EventTemplate[], metadataEntities: MetaEntity[]): EventWithMeta[] {
  return linkToMetadata('event_template', templates, metadataEntities)
}

export function linkToMetadata<T extends ApiEntity>(
  entity_type: string,
  primaryEntities: T[],
  metadataEntities: MetaEntity[],
): WithMeta<T>[] {
  const result = {}

  for (const original of primaryEntities) {
    const parent = { ...original } as WithMeta<T>
    parent.meta = {}
    result[parent.id] = parent
  }

  for (const { resource, resource_id, metakey, value } of metadataEntities) {
    if (resource === entity_type) {
      const parent = result[resource_id]
      if (parent) {
        parent.meta[metakey] = value
      }
    }
  }

  return Object.values(result)
}

export function linkRelatedEntities(
  parents: NamedApiEntity[],
  children: NamedApiEntity[],
  relation: string,
  collection: string,
) {
  const result = {}

  for (const original of parents) {
    const parent = { ...original }
    parent[collection] = []
    result[parent.id] = parent
  }

  for (const child of children) {
    const parentId = child[relation]
    const parent = result[parentId]
    if (parent) {
      parent[collection].push(child)
    }
  }

  return Object.values(result)
}

export function firstEntity<
  Response extends ApiResponse<any>,
  Name extends keyof Response,
  Result extends Response[Name]['_data'][0][],
>(response: Response, resourceName: Name, ...more: Name[]): Result {
  const result: ApiEntity[] = []

  // At least one resource name is required.
  for (const resource of [resourceName, ...more]) {
    result.push(response[resource]._data[0])
  }

  return result as Result
}

export function apiEntities<
  Response extends ApiResponse<any>,
  Result extends { [Key in keyof Response]: Response[Key]['_data'] },
>(response: Response): Result {
  const result: Dict<ApiEntity[]> = {}

  for (const [key, value] of Object.entries(response)) {
    // Ignore meta properties like _limit and _total.
    if (!key.startsWith('_')) {
      result[key as EntityTypeName] = value._data
    }
  }

  return result as Result
}
